import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Send, Phone, Mail, MapPin } from "lucide-react";

const SERVICE_OPTIONS = [
    "Driveway",
    "Sidewalk / Walkway",
    "Patio",
    "Stamped Concrete",
    "Addition / Extension",
    "Cement Curbs",
    "Other",
];

export default function QuoteForm({ contact, services }) {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        service: "",
        address: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone) {
            toast.error("Please share your name and phone so we can call back.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/leads", {
                ...form,
                email: form.email || null,
            });
            toast.success("Got it! We'll reach out within 24 hours.");
            setDone(true);
            setForm({ name: "", phone: "", email: "", service: "", address: "", message: "" });
        } catch (err) {
            toast.error("Something went wrong. Please call (719) 678-4639.");
        } finally {
            setLoading(false);
        }
    };

    const serviceOptions = services?.length
        ? services.map((s) => s.title).concat("Other")
        : SERVICE_OPTIONS;

    return (
        <section id="quote" className="relative bg-[#0a0a0a] border-t border-zinc-900">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* LEFT — pitch + contact */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-yellow-400" />
                            <span className="font-mono-accent text-[11px] text-yellow-400">
                                05 / Free Quote
                            </span>
                        </div>
                        <h2
                            data-testid="quote-heading"
                            className="font-display text-white text-5xl md:text-6xl lg:text-7xl"
                        >
                            Get a real <br />
                            <span className="text-yellow-400">quote</span> in 24h.
                        </h2>
                        <p className="mt-8 text-zinc-400 text-base md:text-lg leading-relaxed">
                            Fill in the form or call us directly. We&apos;ll walk your site, measure the
                            job and send a written estimate — no cost, no obligation.
                        </p>

                        <div className="mt-10 space-y-4">
                            <a
                                href={`tel:${contact?.phone?.replace(/[^\d+]/g, "")}`}
                                data-testid="contact-phone-block"
                                className="flex items-center gap-4 border border-zinc-800 hover:border-yellow-400 p-5 transition-colors group"
                            >
                                <div className="w-12 h-12 border border-yellow-400 flex items-center justify-center group-hover:bg-yellow-400 transition-colors">
                                    <Phone className="w-5 h-5 text-yellow-400 group-hover:text-black" />
                                </div>
                                <div>
                                    <div className="font-mono-accent text-[10px] text-zinc-500">
                                        CALL / TEXT
                                    </div>
                                    <div className="font-display text-white text-2xl">
                                        {contact?.phone}
                                    </div>
                                </div>
                            </a>
                            <a
                                href={`mailto:${contact?.email}`}
                                data-testid="contact-email-block"
                                className="flex items-center gap-4 border border-zinc-800 hover:border-yellow-400 p-5 transition-colors group"
                            >
                                <div className="w-12 h-12 border border-yellow-400 flex items-center justify-center group-hover:bg-yellow-400 transition-colors">
                                    <Mail className="w-5 h-5 text-yellow-400 group-hover:text-black" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-mono-accent text-[10px] text-zinc-500">
                                        EMAIL
                                    </div>
                                    <div className="text-white text-base md:text-lg break-all">
                                        {contact?.email}
                                    </div>
                                </div>
                            </a>
                            <div className="flex items-center gap-4 border border-zinc-800 p-5">
                                <div className="w-12 h-12 border border-yellow-400 flex items-center justify-center">
                                    <MapPin className="w-5 h-5 text-yellow-400" />
                                </div>
                                <div>
                                    <div className="font-mono-accent text-[10px] text-zinc-500">
                                        SERVICE AREA
                                    </div>
                                    <div className="text-white text-base md:text-lg">
                                        {contact?.service_area}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — form */}
                    <div className="lg:col-span-7">
                        <form
                            onSubmit={submit}
                            data-testid="quote-form"
                            className="border border-zinc-900 bg-[#050505] p-6 md:p-10 relative"
                        >
                            <div className="absolute top-0 left-0 h-1 w-16 bg-yellow-400" />
                            {done ? (
                                <div className="text-center py-16" data-testid="quote-success">
                                    <div className="font-display text-yellow-400 text-6xl md:text-7xl">
                                        Thanks!
                                    </div>
                                    <p className="mt-4 text-zinc-300 text-lg">
                                        We received your request and will reach out within 24 hours.
                                    </p>
                                    <button
                                        onClick={() => setDone(false)}
                                        className="mt-8 font-mono-accent text-xs text-yellow-400 hover:underline"
                                    >
                                        [ SUBMIT ANOTHER ]
                                    </button>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 gap-5">
                                    <div className="md:col-span-1">
                                        <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">
                                            NAME *
                                        </label>
                                        <input
                                            data-testid="quote-input-name"
                                            required
                                            value={form.name}
                                            onChange={set("name")}
                                            placeholder="Your full name"
                                            className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none text-white px-4 py-3 placeholder:text-zinc-700 transition-colors"
                                        />
                                    </div>
                                    <div className="md:col-span-1">
                                        <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">
                                            PHONE *
                                        </label>
                                        <input
                                            data-testid="quote-input-phone"
                                            required
                                            type="tel"
                                            value={form.phone}
                                            onChange={set("phone")}
                                            placeholder="(719) 555-1234"
                                            className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none text-white px-4 py-3 placeholder:text-zinc-700 transition-colors"
                                        />
                                    </div>
                                    <div className="md:col-span-1">
                                        <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">
                                            EMAIL
                                        </label>
                                        <input
                                            data-testid="quote-input-email"
                                            type="email"
                                            value={form.email}
                                            onChange={set("email")}
                                            placeholder="you@example.com"
                                            className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none text-white px-4 py-3 placeholder:text-zinc-700 transition-colors"
                                        />
                                    </div>
                                    <div className="md:col-span-1">
                                        <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">
                                            SERVICE
                                        </label>
                                        <select
                                            data-testid="quote-input-service"
                                            value={form.service}
                                            onChange={set("service")}
                                            className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none text-white px-4 py-3 transition-colors"
                                        >
                                            <option value="">Select…</option>
                                            {serviceOptions.map((o) => (
                                                <option key={o} value={o}>
                                                    {o}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">
                                            JOB ADDRESS
                                        </label>
                                        <input
                                            data-testid="quote-input-address"
                                            value={form.address}
                                            onChange={set("address")}
                                            placeholder="Street, city, ZIP"
                                            className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none text-white px-4 py-3 placeholder:text-zinc-700 transition-colors"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">
                                            PROJECT DETAILS
                                        </label>
                                        <textarea
                                            data-testid="quote-input-message"
                                            rows={5}
                                            value={form.message}
                                            onChange={set("message")}
                                            placeholder="Approximate size, timeline, any photos or references…"
                                            className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none text-white px-4 py-3 placeholder:text-zinc-700 transition-colors resize-none"
                                        />
                                    </div>
                                    <div className="md:col-span-2 pt-2">
                                        <button
                                            data-testid="quote-submit-button"
                                            disabled={loading}
                                            className="w-full md:w-auto bg-yellow-400 text-black font-black uppercase tracking-wider px-8 py-5 text-sm hover:bg-yellow-300 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-3 justify-center transition-colors"
                                        >
                                            {loading ? "Sending…" : "Send Quote Request"}
                                            <Send className="w-4 h-4" />
                                        </button>
                                        <p className="mt-4 font-mono-accent text-[10px] text-zinc-600">
                                            WE RESPOND WITHIN 24 HOURS · MON–SAT
                                        </p>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
